// GG Bharat is more than one game: the stage where our worlds live. Games you play, shows you watch together, events
// you take part in, all from catalog.ts. A kind with nothing posted yet says so plainly (and how to hear first), so
// the page never claims a show that doesn't exist. And a line for artists and brands who'd like to put one on.
import { BUSINESS } from '../brand';
import { byKind, hrefOf, KINDS, STATUS_LABEL, type Kind } from '../catalog';
import { linkProps } from '../router';

/** paper-cut pictures for each kind: a stage with its curtains, a festival night, and for games the city itself */
function Art({ kind }: { kind: Kind }) {
  if (kind === 'show')
    return (
      <svg viewBox="0 0 320 200" className="st-art" aria-hidden>
        <rect width="320" height="200" fill="#1c0f0a" />
        <path d="M110 200 L160 36 L210 200Z" fill="rgba(255,214,140,.16)" />
        <ellipse cx="160" cy="170" rx="70" ry="12" fill="rgba(255,214,140,.22)" />
        <path d="M0 0H320V26Q300 40 280 26Q260 40 240 26Q220 40 200 26Q180 40 160 26Q140 40 120 26Q100 40 80 26Q60 40 40 26Q20 40 0 26Z" fill="#b8324a" />
        <path d="M0 22C30 60 34 150 0 200Z" fill="#9b2440" />
        <path d="M320 22C290 60 286 150 320 200Z" fill="#9b2440" />
        <path d="M0 22C46 70 52 150 22 200H0Z" fill="#c23a52" opacity=".85" />
        <path d="M320 22C274 70 268 150 298 200H320Z" fill="#c23a52" opacity=".85" />
        <rect y="176" width="320" height="24" fill="#2e180e" />
        {Array.from({ length: 13 }, (_, i) => (
          <circle key={i} cx={14 + i * 24.5} cy={186 + (i % 2) * 3} r="9" fill="#0e0705" />
        ))}
        <circle cx="160" cy="128" r="9" fill="#0e0705" />
        <path d="M150 170 Q160 120 170 170Z" fill="#0e0705" />
      </svg>
    );
  if (kind === 'event')
    return (
      <svg viewBox="0 0 320 200" className="st-art" aria-hidden>
        <rect width="320" height="200" fill="#140c08" />
        {[
          [78, 62, 30, '#ffb347'],
          [210, 46, 38, '#ff6f91'],
          [262, 98, 22, '#7fd1ff'],
          [140, 96, 18, '#ffe08a'],
        ].map(([x, y, r, c], i) => (
          <g key={i} stroke={c as string} strokeWidth="2.4" strokeLinecap="round">
            {Array.from({ length: 12 }, (_, k) => {
              const a = (k / 12) * Math.PI * 2;
              const rr = r as number;
              return <line key={k} x1={(x as number) + Math.cos(a) * rr * 0.35} y1={(y as number) + Math.sin(a) * rr * 0.35} x2={(x as number) + Math.cos(a) * rr} y2={(y as number) + Math.sin(a) * rr} />;
            })}
          </g>
        ))}
        <path d="M0 150 L40 128 L70 140 L110 112 L150 134 L190 118 L240 138 L280 120 L320 136 V200 H0Z" fill="#2c1a11" />
        <path d="M0 168 H320 V200 H0Z" fill="#3a2214" />
        {Array.from({ length: 9 }, (_, i) => (
          <g key={i} transform={`translate(${22 + i * 35} 170)`}>
            <path d="M-11 0 Q0 12 11 0Z" fill="#c4682c" />
            <path d="M0 -14 Q6 -6 0 -1 Q-6 -6 0 -14Z" fill="#ffd27a" />
          </g>
        ))}
      </svg>
    );
  return <img className="st-art" src="/site/shots/aerial.webp" alt="" loading="lazy" />;
}

export function Stage() {
  return (
    <section className="gx-sec st" id="stage" aria-labelledby="st-title">
      <header className="gx-head rv">
        <p className="gx-kicker mono">04 · More than a game</p>
        <h2 id="st-title">
          A stage for India,
          <br />
          <em>in 3D.</em>
        </h2>
        <p className="dim">GG Bharat is where our worlds live: games you play, shows you watch together, events you take part in. One account, one name, every world.</p>
      </header>
      <div className="st-grid">
        {KINDS.map((k, i) => {
          const items = byKind(k.kind);
          return (
            <article key={k.kind} className={`st-card ${k.kind} rv`} style={{ '--d': `${i * 0.08}s` } as React.CSSProperties}>
              <div className="st-stub mono" aria-hidden>
                <span>{k.plural}</span>
                <span>№ {String(i + 1).padStart(2, '0')}</span>
              </div>
              <div className="st-body">
                <div className="st-pic">
                  <Art kind={k.kind} />
                </div>
                <h3>{k.plural}</h3>
                <p className="pitch">{k.pitch}</p>
                {items.length ? (
                  <ul className="st-list">
                    {items.map((e) => (
                      <li key={e.id}>
                        <a {...linkProps(hrefOf(e))}>
                          <b>{e.title}</b>
                          <span>{e.tagline}</span>
                          <em className={`mono st-status ${e.status}`}>{STATUS_LABEL[e.status]}</em>
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="st-empty mono">{k.empty}</p>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <p className="st-host rv">
        <span className="mono">For artists, creators and brands</span>
        Want to put on a show or an event in one of our worlds? <a href={`mailto:${BUSINESS.email}?subject=${encodeURIComponent('A show or event on GG Bharat')}`}>Write to us</a>.
      </p>
    </section>
  );
}
